import { RequestApi } from '../helpers';
import {
  IDrugTestingScheduleRequestBody,
  ICreateThirdPartyDrugTestResultBody,
  IUpdateThirdPartyDrugTestResultBody,
  IThirdPartyDocumentPatchBody,
} from '../models/drugTesting';

const getProjects = () => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/Projects?ClientSidePaging=true`);
  // return RequestApi.get(`/Projects`);
};

const searchProjects = (searchText: string) => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/Projects/search?ProjectStatuses=2&nameContains=${searchText}`);
};

const getProject = (projectId: string) => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/projects/${projectId}`);
};

const getProjectLite = (projectId: string) => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/projects/lite/${projectId}`);
};

const getProjectSettings = (projectId: string) => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/Projects/${projectId}/GetWorkerProjectSetting`);
};

const getWhitelistedProjects = () => {
  RequestApi.defaults.headers['Accept-Language'] = 'en';
  return RequestApi.get(`/public/projects/sourceApp/whiteListed?EntityType=Project&SourceApp=Freedom-Worker-Portal`);
};

const getWorkerProfileCustomFields = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`public/workers/workerProfileCustomFields`, data);
};

const getProfileCustomFieldsConfig = (
  body: {
    workerId: string;
    companyId?: string;
    projectId?: string;
    allProjectsSetting?: boolean;
    showAcceptedProjectsSettingsOnly?: boolean;
  } | null,
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`workers/WorkerProfileCustomFields`, {
    allProjectsSetting: true,
    ...(body || {}),
  });
};

const getWorkerProfileFieldsForAllCompanies = (
  projectId: string,
  languageCode: string = 'en',
  isPublic: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  // The authenticated route requires a token (JAG-609); callers with no session use the public one.
  return RequestApi.get(
    isPublic
      ? `/public/Projects/${projectId}/WorkerProfileFields`
      : `/Workers/project/${projectId}/WorkerProfileFieldsForAllCompanies`,
  );
};

const getAppLanguages = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/public/languages?getApplicationLanguagesOnly=true`);
};

const getCompanies = (projectId, languageCode: string = 'en', isPublic: boolean = true) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const publicEndpoint = isPublic ? 'public/' : '';
  return RequestApi.get(
    `/${publicEndpoint}Projects/${projectId}/companies?ExcludePendingProjectClients=true&ExcludeInvitationSentClients=true&ClientSidePaging=true&UseGCWorkerControlSettings=true&GCCompanyAccessType=3&SortCriteria=name asc`,
  );
};

const getEthnicities = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Ethnicities`);
};

const getLanguages = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Languages`);
};

const getCountries = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Countries`);
};

const getStates = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/GeographicLocations`);
};

const getIDTypes = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/identificationTypes`);
};

const getProjectRoles = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/ProjectRoles`);
};

const getSkilledTrades = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/SkilledTrades`);
};

const getWorker = (id: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Workers/${id}?excludeAccessFilter=true`);
  // return RequestApi.get(`/Workers/${id}`);
};

const getWorkerExistingConsentForms = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Workers/${workerId}/projects/${projectId}/consentFormForAllCompanies`);
};

const postWorker = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`public/workers`, data);
};

const updateWorker = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.put(`workers/${workerId}?version=v2`, data);
};

const validatePin = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Users/mobile/ValidatePin`, data);
};

const resendPin = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Workers/resendPin`, data);
};

const register = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/users/mobile/register`, data);
};

const uploadImage = (workerUserId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/users/${workerUserId}/uploadPicture`, data);
};

const uploadImageSelf = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/users/self/uploadPicture`, data);
};

const uploadWorkerImage = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`workers/${workerId}/uploadPicture`, data);
};

const uploadSignature = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Workers/${workerId}/uploadSignature`, data);
};

const confirmImageUpload = (fileId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Files/${fileId}/confirm`);
};

const getProfileCustomFields = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`Workers/${workerId}/projects/${projectId}/profileCustomFields`);
};

const uploadGovernmentDocuments = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`Workers/${workerId}/GovernmentDocuments/upload`, data);
};

const deleteGovernmentDocument = (workerId: string, documentId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.delete(`Workers/${workerId}/governmentDocuments/${documentId}/files`);
};

const updateWorkerBiometricConsent = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.put(`/Workers/${workerId}/WorkerBiometricConsent`, data);
};

const updateProfile = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.put(`/workers/${workerId}?version=v2`, data);
};

const assignWorkerToProject = (projectId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Projects/${projectId}/workers/assign`, data);
};

const getCertificationTypes = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Certifications`);
};
const getCertificationGroups = (projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Projects/${projectId}/certificationGroups`);
};

const fetchCertificationGroups = (projectId: string, workerId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Projects/${projectId}/certificationGroups?workerId=${workerId}`);
};

const getAllCertifications = (workerId: string, languageCode: string = 'en', isWorker: boolean = false) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.get(`/Workers/self/certifications`);
  }
  return RequestApi.get(`/Workers/${workerId}/certifications`);
};

const getMyCertifications = (
  workerId: string,
  params: {
    pageNumber: number;
    pageSize: number;
    filterCertificateStatus: number;
    sortCriteria?: string;
    projectIds?: string[];
    filterCertificateRelatedTo?: number;
  },
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;

  let queryString = `pageNumber=${params.pageNumber}&pageSize=${params.pageSize}&FilterCertificateStatus=${params.filterCertificateStatus}`;
  if (params.sortCriteria) {
    queryString += `&sortCriteria=${params.sortCriteria}`;
  }
  if (params.projectIds && params.projectIds.length > 0) {
    params.projectIds.forEach(id => {
      queryString += `&projectids=${id}`;
    });
  }
  if (params.filterCertificateRelatedTo !== undefined) {
    queryString += `&FilterCertificateRelatedTo=${params.filterCertificateRelatedTo}`;
  }

  return RequestApi.get(`/Workers/${workerId}/certifications/v2?${queryString}`);
};

const getRequiredCertifications = (
  workerId: string,
  params: {
    pageNumber: number;
    pageSize: number;
    projectIds?: string[];
    sortCriteria?: string;
  },
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;

  let queryString = `pageNumber=${params.pageNumber}&pageSize=${params.pageSize}`;
  if (params.projectIds && params.projectIds.length > 0) {
    params.projectIds.forEach(id => {
      queryString += `&projectIds=${id}`;
    });
  }
  if (params.sortCriteria) {
    queryString += `&sortCriteria=${params.sortCriteria}`;
  }

  return RequestApi.get(`/Workers/${workerId}/required/certifications?${queryString}`);
};

const addCertification = (workerId: string, data: any, languageCode: string = 'en', isWorker: boolean = false) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.post(`/Workers/self/certifications`, data);
  }
  return RequestApi.post(`/Workers/${workerId}/certifications`, data);
};

const updateCertification = (
  workerId: string,
  workerCertificationId: string,
  data: any,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.put(`/Workers/self/certifications/${workerCertificationId}`, data);
  }
  return RequestApi.put(`/Workers/${workerId}/certifications/${workerCertificationId}`, data);
};

const getCertification = (
  workerId: string,
  workerCertificationId: string,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.get(`/Workers/self/certifications/${workerCertificationId}`);
  }
  return RequestApi.get(`/Workers/${workerId}/certifications/${workerCertificationId}`);
};

const uploadCertificate = (
  workerId: string,
  workerCertificationId: string,
  data: any,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.post(`/Workers/self/certifications/${workerCertificationId}/upload`, data);
  }
  return RequestApi.post(`/Workers/${workerId}/certifications/${workerCertificationId}/upload`, data);
};

const deleteCertification = (
  workerId: string,
  workerCertificationId: string,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.delete(`/Workers/self/certifications/${workerCertificationId}`);
  }
  return RequestApi.delete(`/Workers/${workerId}/certifications/${workerCertificationId}`);
};

const deleteCertificationFiles = (
  workerId: string,
  workerCertificationId: string,
  data: any,
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.delete(`/Workers/${workerId}/certifications/${workerCertificationId}/files`, {
    data,
  });
};

// const getConsentFormFields = (languageCode: string = "en") => {
//   RequestApi.defaults.headers["Accept-Language"] = languageCode;
//   return RequestApi.get(`/ConsentFormFields`);
// };

const getConsentFormFields = (languageCode: string = 'en', projectId?: string) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(
    `/ConsentFormFields${
      projectId && projectId.length > 0
        ? `?projectId=${projectId}&IncludeOnlyGCsFields=true&IncludeUsageCount=false`
        : `?IncludeUsageCount=false`
    }`,
  );
};

const getConsentAgreements = ({
  projectId,
  workerId,
  languageCode,
}: { projectId?: string; workerId?: string; languageCode?: string } = {}) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode || 'en';
  let query = ``;
  if (projectId) {
    query += `projectId=${projectId}`;
  }
  if (workerId) {
    query += `&workerId=${workerId}`;
  }
  return RequestApi.get(
    `workers/consentAgreements?${query}`,
    // { headers: { 'Source-App': '' } }
  );
};

const acceptConsentAgreements = (workerId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Workers/${workerId}/ConsentAgreements/Accept`, data);
};

const getJobTitles = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/JobTitles`);
};

const getTradeStatus = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/TradeStatus`);
};

const getSocJobTitles = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/SocJobTitles`);
};

const getLanguageTurnerProtocol = (languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/LanguageTurnerProtocol`);
};

const acceptProject = (
  workerUserId: string,
  projectId: string,
  companyId: string,
  data: any,
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(
    // `Workers/${workerUserId}/projects/${projectId}/accept`,
    `Workers/${workerUserId}/projects/${projectId}/company/${companyId}/accept`,
    data,
  );
};

const declineProject = (projectId: string, data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`Workers/projects/${projectId}/decline`, data);
};

const sendAccessPin = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`Users/sendAccessPin`, data);
};

const validateAccessPin = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`Users/ValidateAccessPin`, data);
};

const getWorkerStatus = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`Workers/${workerId}/project/${projectId}`);
};

const searchWorker = (query: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`public/workers/SearchDuplicates?${query}`);
};

const searchWorkerV2 = (query: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`Workers/search/v2?${query}`);
};

const getWorkerProjectCompanies = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`Workers/${workerId}/project/${projectId}/CompanyNames`);
};

const generatePinForPiiVerification = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`workers/generateWorkerPIIUpdatePin`, data);
};

const validatePinForPiiVerification = (data: any, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`workers/validateWorkerPIIPin`, data);
};

const getWorkerLiteProjects = (
  workerId: string,
  pageNumber: number = 1,
  pageSize?: number,
  status?: number,
  sort?: string,
  languageCode: string = 'en',
  clientSidePaging: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
  });
  if (pageSize !== undefined) {
    params.append('pageSize', pageSize.toString());
  }
  if (clientSidePaging) {
    params.append('ClientSidePaging', 'true');
  }
  if (status !== undefined) {
    if (status === 3) {
      params.append('projectStatus', '3');
    } else if (status < 3) {
      params.append('status', status.toString());
    }
  }
  if (sort) {
    params.append('sortCriteria', sort);
  }
  return RequestApi.get(`/workers/${workerId}/lite/projects?${params.toString()}`);
};

const getConsentForm = (workerId: string, projectId: string, companyId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const params = companyId ? `?companyId=${companyId}` : '';
  return RequestApi.get(`workers/${workerId}/projects/${projectId}/consentForm${params}`);
};

const fetchConsentFormPdf = (workerId: string, projectId: string, companyId?: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const params = companyId ? `?companyId=${companyId}` : '';
  return RequestApi.get(`workers/${workerId}/projects/${projectId}/consentForm/pdf${params}`, { responseType: 'blob' });
};

const fetchBadgePdf = (id: string, isWorker: boolean, templateId?: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const projectBadgeTemplateId = templateId ? `projectBadgeTemplateId=${templateId}&` : '';
  let path = `/badges/${id}/pdf?${projectBadgeTemplateId}trackHistory=false`;
  if (isWorker) {
    path = `/workers/self/badges/${id}/pdf?${projectBadgeTemplateId}trackHistory=false`;
  }

  return RequestApi.get(path, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/pdf',
      Accept: '*/*',
    },
  });
};

const getWorkerRequirementStatus = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/workers/${workerId}/project/${projectId}/requirementStatus`);
};

const getRevokeBadgeInfo = (workerId: string, projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/Workers/${workerId}/Projects/${projectId}/revokeBadgeInfo`);
};

const getPositiveDrugTestResults = (
  workerId: string,
  projectId: string,
  companyIds: string[],
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/Workers/${workerId}/Project/${projectId}/PositiveDrugTestResults`, companyIds);
};

const getWorkerProfilePictures = (workerId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`Workers/${workerId}/profilePictures`);
};

const getWorkerCompaniesRequirements = (
  workerId: string,
  projectId: string,
  companyIds: string[],
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const params = new URLSearchParams();
  params.append('projectId', projectId);
  companyIds.forEach(id => params.append('CompanyIds', id));
  return RequestApi.get(`/workers/${workerId}/companiesRequirements?${params.toString()}`);
};

const getWorkerDrugTestingSchedules = (
  workerId: string,
  params: {
    pageNumber: number;
    pageSize: number;
    projectId?: string;
  },
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;

  // Worker session uses the /self endpoint (camelCase params); admin/by-id uses /DrugTesting/{workerId}.
  if (isWorker) {
    let queryString = `pageNumber=${params.pageNumber}&pageSize=${params.pageSize}`;
    if (params.projectId) {
      queryString += `&projectId=${params.projectId}`;
    }
    return RequestApi.get(`/workers/self/WorkerDrugTestingSchedules?${queryString}`);
  }

  let queryString = `PageNumber=${params.pageNumber}&PageSize=${params.pageSize}`;
  if (params.projectId) {
    queryString += `&ProjectId=${params.projectId}`;
  }
  return RequestApi.get(`/DrugTesting/${workerId}/WorkerDrugTestingSchedules?${queryString}`);
};

const getDrugTestingFacilitiesGroupedByState = (
  projectId: string,
  params: {
    pageNumber: number;
    pageSize: number;
  },
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(
    `/DrugTestingFacilities/groupedByState?pageNumber=${params.pageNumber}&pageSize=${params.pageSize}&projectId=${projectId}`,
  );
};

const getProjectDrugTestingTypes = (projectId: string, languageCode: string = 'en') => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.get(`/projects/${projectId}/drugTestingTypes`);
};

const getFacilityTestPanelsByFacilityIds = (
  body: {
    facilityIds: string[];
    projectId: string;
  },
  languageCode: string = 'en',
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  return RequestApi.post(`/DrugTestingFacilities/GetFacilityTestPanelsByFacilityIds`, body);
};

const getDrugTestingFacility = (facilityId: string, languageCode: string = 'en', isWorker: boolean = false) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.get(`/Workers/Self/Facility/${facilityId}/Details`);
  }
  return RequestApi.get(`/DrugTestingFacilities/${facilityId}`);
};

// Shapes the flat input into the API body.
// Body is pre-shaped by buildDrugTestingScheduleBody in the workerData thunks; isWorker only selects the endpoint.
const createDrugTestingSchedule = (
  body: IDrugTestingScheduleRequestBody,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;

  if (isWorker) {
    return RequestApi.post(`/workers/self/WorkerDrugTestingSchedules`, body);
  }
  return RequestApi.post(`/WorkerDrugTestingSchedules`, body);
};

const updateDrugTestingSchedule = (
  scheduleId: string,
  body: IDrugTestingScheduleRequestBody,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;

  if (isWorker) {
    return RequestApi.put(`/workers/self/DrugTestingSchedule/${scheduleId}`, body);
  }
  return RequestApi.put(`/DrugTesting/${scheduleId}/WorkerDrugTestingSchedules`, body);
};

const deleteDrugTestingSchedule = (scheduleId: string, languageCode: string = 'en', isWorker: boolean = false) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) return RequestApi.delete(`/workers/self/WorkerDrugTestingSchedules/${scheduleId}`);
  return RequestApi.delete(`/WorkerDrugTestingSchedules/${scheduleId}`);
};

const createThirdPartyDrugTestResult = (
  body: ICreateThirdPartyDrugTestResultBody,
  languageCode: string = 'en',
  isWorker: boolean = false,
  workerId?: string,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.post(`/workers/self/ThirdPartyDrugTestResults`, body);
  }
  return RequestApi.post(`/Workers/${workerId}/ThirdPartyDrugTestResults`, body);
};

const updateThirdPartyDrugTestResult = (
  resultId: string,
  body: IUpdateThirdPartyDrugTestResultBody,
  languageCode: string = 'en',
  isWorker: boolean = false,
  workerId?: string,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.put(`/workers/self/ThirdPartyDrugTestResults/${resultId}`, body);
  }
  return RequestApi.put(`/Workers/${workerId}/ThirdPartyDrugTestResults/${resultId}`, body);
};

const deleteThirdPartyDrugTestResult = (
  resultId: string,
  languageCode: string = 'en',
  isWorker: boolean = false,
  workerId?: string,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.delete(`/workers/self/ThirdPartyDrugTestResults/${resultId}`);
  }
  return RequestApi.delete(`/Workers/${workerId}/ThirdPartyDrugTestResults/${resultId}`);
};

const patchThirdPartyDrugTestResultDocuments = (
  resultId: string,
  body: IThirdPartyDocumentPatchBody,
  languageCode: string = 'en',
  isWorker: boolean = false,
  workerId?: string,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.patch(`/workers/self/ThirdPartyDrugTestResults/${resultId}/documents`, body);
  }
  return RequestApi.patch(`/Workers/${workerId}/ThirdPartyDrugTestResults/${resultId}/documents`, body);
};

const getWorkerThirdPartyDrugTestResults = (
  workerId: string,
  params: { pageNumber: number; pageSize: number },
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  const queryString = `PageNumber=${params.pageNumber}&PageSize=${params.pageSize}`;
  if (isWorker) {
    return RequestApi.get(`/workers/self/ThirdPartyDrugTestResults?${queryString}`);
  }
  return RequestApi.get(`/Workers/${workerId}/ThirdPartyDrugTestResults?${queryString}`);
};

// Full worker-project detail; the Upload Results modal reads drugTestingPanels/Types from it.
const getWorkerProjectDetail = (
  workerId: string,
  workerProjectId: string,
  languageCode: string = 'en',
  isWorker: boolean = false,
) => {
  RequestApi.defaults.headers['Accept-Language'] = languageCode;
  if (isWorker) {
    return RequestApi.get(`/workers/self/projects/${workerProjectId}`);
  }
  return RequestApi.get(`/Workers/${workerId}/WorkerProjects/${workerProjectId}`);
};

const Services = {
  getProjects,
  getWhitelistedProjects,
  searchProjects,
  getProject,
  getProjectLite,
  getProjectSettings,
  getWorkerProfileCustomFields,
  getProfileCustomFieldsConfig,
  getWorkerProfileFieldsForAllCompanies,
  getAppLanguages,
  getCompanies,
  getEthnicities,
  getLanguages,
  getCountries,
  getStates,
  getIDTypes,
  getProjectRoles,
  getSkilledTrades,
  getWorker,
  getWorkerExistingConsentForms,
  postWorker,
  updateWorker,
  validatePin,
  resendPin,
  register,
  uploadImage,
  uploadImageSelf,
  uploadWorkerImage,
  uploadSignature,
  confirmImageUpload,
  deleteCertification,
  deleteCertificationFiles,
  getProfileCustomFields,
  uploadGovernmentDocuments,
  deleteGovernmentDocument,
  updateWorkerBiometricConsent,
  updateProfile,
  assignWorkerToProject,
  getCertificationTypes,
  getCertificationGroups,
  fetchCertificationGroups,
  getAllCertifications,
  getMyCertifications,
  getRequiredCertifications,
  addCertification,
  updateCertification,
  getCertification,
  uploadCertificate,
  getConsentFormFields,
  getConsentAgreements,
  acceptConsentAgreements,
  getJobTitles,
  getTradeStatus,
  getSocJobTitles,
  getLanguageTurnerProtocol,
  acceptProject,
  declineProject,
  sendAccessPin,
  validateAccessPin,
  getWorkerStatus,
  searchWorker,
  searchWorkerV2,
  getWorkerProjectCompanies,
  generatePinForPiiVerification,
  validatePinForPiiVerification,
  getWorkerLiteProjects,
  getConsentForm,
  fetchConsentFormPdf,
  fetchBadgePdf,
  getWorkerRequirementStatus,
  getRevokeBadgeInfo,
  getPositiveDrugTestResults,
  getWorkerProfilePictures,
  getWorkerCompaniesRequirements,
  getWorkerDrugTestingSchedules,
  getDrugTestingFacilitiesGroupedByState,
  getProjectDrugTestingTypes,
  getFacilityTestPanelsByFacilityIds,
  getDrugTestingFacility,
  createDrugTestingSchedule,
  updateDrugTestingSchedule,
  deleteDrugTestingSchedule,
  createThirdPartyDrugTestResult,
  updateThirdPartyDrugTestResult,
  deleteThirdPartyDrugTestResult,
  patchThirdPartyDrugTestResultDocuments,
  getWorkerThirdPartyDrugTestResults,
  getWorkerProjectDetail,
};

export default Services;
